/**
 * پیکربندیِ از راه دور، سمتِ کلاینت.
 *
 * سرور یک تصویرِ **منتشرشده** نگه می‌دارد و این‌جا سرِ بالا آمدن یک بار
 * خوانده می‌شود. از آن به بعد `flag`/`num`/`str`/`json` همگام‌اند، چون کدِ
 * بازی نباید برای یک پرچم `await` کند.
 *
 * (نامش `remote` است و نه `config`، چون `net/config.ts` از قبل هست و
 * نشانیِ سرور و شناسه‌ی دستگاه را می‌دهد. همان نامِ بسته‌ی سمتِ سرور هم
 * هست: `internal/remote`.)
 *
 * # سه قاعده که عمدی‌اند
 *
 * **هیچ‌وقت نمی‌شکند.** شبکه نبود، سرور قدیمی بود، یا هنوز چیزی منتشر
 * نشده — هر سه یعنی «مقدارِ پیش‌فرض». بازی‌ای که بی پیکربندی بالا نیاید،
 * از نبودِ پیکربندی بدتر است.
 *
 * **پیش‌فرض اجباری است.** `flag("x")` نداریم، `flag("x", true)` داریم:
 * کلیدی که هنوز ساخته نشده و کلیدی که خاموش است باید در خودِ کد از هم جدا
 * باشند.
 *
 * **بارگذاری جلوی رندر را نمی‌گیرد.** صفحه بالا می‌آید و پیکربندی وقتی
 * رسید می‌نشیند؛ هرچه پیش از رسیدنش خوانده شود پیش‌فرض می‌گیرد.
 */
import { httpUrl } from "./config";

type Values = Record<string, unknown>;

let values: Values = {};
let version = 0;

/** شماره‌ی نسخه‌ی منتشرشده‌ای که گرفتیم. صفر = هنوز چیزی نیامده. */
export const configVersion = (): number => version;

/**
 * پیکربندی را بگیر.
 *
 * توکن اگر باشد فرستاده می‌شود: قاعده‌های درصدی بی شناسه‌ی بازیکن نمی‌خورند
 * و ناشناس مقدارِ پایه می‌گیرد — که پایدار است و شیر-یا-خط نیست.
 */
export async function loadRemote(token?: string): Promise<void> {
  try {
    const r = await fetch(httpUrl("/v1/config?platform=web"), {
      headers: token ? { authorization: `Bearer ${token}` } : undefined,
    });
    if (!r.ok) return;
    const body = (await r.json()) as { version?: number; values?: Values };
    if (body.values && typeof body.values === "object") values = body.values;
    version = typeof body.version === "number" ? body.version : 0;
  } catch {
    // شبکه نبود. پیش‌فرض‌ها سرِ جایشان‌اند.
  }
}

/**
 * تایپ را **می‌سنجیم** و نه فقط `as` می‌زنیم.
 *
 * مقدارِ کلیدی که در پنل جنسش عوض شده باشد، وگرنه بی‌صدا به جای عدد رشته
 * می‌شود و جای دیگری — دور از این‌جا — می‌ترکد.
 */
export function flag(key: string, fallback: boolean): boolean {
  const v = values[key];
  return typeof v === "boolean" ? v : fallback;
}

export function num(key: string, fallback: number): number {
  const v = values[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function str(key: string, fallback: string): string {
  const v = values[key];
  return typeof v === "string" ? v : fallback;
}

export function json<T>(key: string, fallback: T): T {
  const v = values[key];
  return v === undefined || v === null ? fallback : (v as T);
}

/** فقط برای آزمون. */
export function __setRemote(v: Values, ver = 1): void {
  values = v;
  version = ver;
}
